<template>
  <v-app id="app_user_registration">
    <v-overlay v-model="centerLoading" :scrim="false" class="custom_centered_overlay">
      <v-progress-circular color="#2A8200" indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <v-container fluid class="pa-8">
      <v-row align="center">
        <v-col cols="7">
          <p class="custom_page_main_title mb-0">USER REGISTRATION</p>
          <p class="custom_page_main_sub_title" style="opacity: 0.6">
            Register users details according to your requirements.
          </p>
        </v-col>
        <v-col cols="5" class="text-right">
          <v-divider :thickness="3" class="divider-vertical" vertical></v-divider>
          <v-btn class="gray-text" size="x-large" variant="text" @click="backPageRoute()">
            <v-icon size="x-large">mdi-menu-left</v-icon> BACK
          </v-btn>
        </v-col>
      </v-row>

      <!-- USER LIMIT INFO BANNER -->
      <v-alert
        v-if="isCurrentCategoryFull"
        type="warning"
        variant="tonal"
        class="mt-4"
        prominent
        border="start"
      >
        <div class="font-weight-bold">{{ limitWarningMessage }}</div>
        <div class="text-caption mt-1">
          Please choose a different user category or contact an administrator to increase the limit.
        </div>
      </v-alert>

      <v-card class="mt-6" elevation="0" border="sm" rounded="lg">
        <v-card-title class="px-6 py-4">
          <span class="text-subtitle-1 font-weight-bold text-uppercase" style="color: #555">Users Registration</span>
        </v-card-title>
        <v-divider></v-divider>

        <v-card-text class="pa-6">
          <v-form ref="form" @submit.prevent>
            <v-row>
              <!-- Left Column -->
              <v-col cols="12" md="6">
                <v-text-field v-model="user.fullName" label="Full Name *" placeholder="Type full name here"
                  variant="outlined" density="comfortable" :rules="fullNameRules" :error-messages="fieldErrors.fullName"
                  maxlength="100" counter>
                </v-text-field>

                <v-text-field v-model="user.userName" label="User Name *" placeholder="Type user name here"
                  variant="outlined" density="comfortable" class="mt-2" :rules="userNameRules"
                  @input="checkUsernameExists" @blur="checkUsernameExists" :error-messages="usernameErrorMessages"
                  maxlength="32" counter>
                </v-text-field>

                <v-text-field v-model="user.emailAddress" label="Email Address *" placeholder="Type email here"
                  variant="outlined" density="comfortable" class="mt-2" :rules="emailRules"
                  :error-messages="emailErrorMessages" maxlength="100" counter @input="checkEmailExists"
                  @blur="checkEmailExists">
                </v-text-field>

                <v-text-field v-model="user.password" :type="showPassword ? 'text' : 'password'" label="Password *"
                  placeholder="Enter password" variant="outlined" density="comfortable" class="mt-2"
                  :rules="passwordRules" :error-messages="fieldErrors.password" maxlength="50" counter>
                  <template v-slot:append-inner>
                    <v-icon :icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'" @click="togglePasswordVisibility"
                      class="cursor-pointer"></v-icon>
                  </template>
                </v-text-field>

                <v-text-field v-model="confirmPassword" :type="showConfirmPassword ? 'text' : 'password'"
                  label="Confirm Password *" placeholder="Confirm your password" variant="outlined"
                  density="comfortable" class="mt-2" :rules="confirmPasswordRules"
                  :error-messages="fieldErrors.confirmPassword" maxlength="50" counter>
                  <template v-slot:append-inner>
                    <v-icon :icon="showConfirmPassword ? 'mdi-eye-off' : 'mdi-eye'"
                      @click="toggleConfirmPasswordVisibility" class="cursor-pointer"></v-icon>
                  </template>
                </v-text-field>

                <v-select v-model="user.userCategory" label="User Category *" placeholder="Select user category"
                  :items="userCategories" item-title="name" item-value="value" variant="outlined" density="comfortable"
                  class="mt-2" :rules="[v => !!v || 'User Category is required']"
                  :error-messages="fieldErrors.userCategory" @update:model-value="onUserCategoryChange">
                </v-select>

                <v-autocomplete v-model="user.userType" :items="filteredUserTypes" item-title="role_type"
                  item-value="id" label="User Role Type *" placeholder="Select user role type" variant="outlined"
                  density="comfortable" class="mt-2" :rules="[v => !!v || 'User Role is required']"
                  :disabled="!user.userCategory || loading.userTypes" :loading="loading.userTypes"
                  :error-messages="fieldErrors.userType" @update:model-value="onUserTypeChange">
                </v-autocomplete>

                <v-row align="center" class="mt-4 mb-4" v-if="showGeofenceOption">
                  <v-col cols="4">
                    <span class="text-subtitle-2 font-weight-bold">ENABLE GEOFENCE</span>
                  </v-col>
                  <v-col cols="8">
                    <v-radio-group v-model="geofenceEnabled" inline hide-details class="custom-inline-radio">
                      <v-radio label="YES" value="yes" color="#7B1FA2"></v-radio>
                      <v-radio label="NO" value="no" color="#7B1FA2" class="ml-8"></v-radio>
                    </v-radio-group>
                  </v-col>
                </v-row>

                <v-btn variant="flat" color="#F3E5F5" class="text-none mb-4"
                  style="color: #7B1FA2; border: 1px solid #7B1FA2" @click="addPhoneNo"
                  :disabled="phoneNumbers.length >= 2">
                  <v-icon start>mdi-plus-circle-outline</v-icon> ADD PHONE NUMBERS
                </v-btn>

                <div v-for="(phone, index) in phoneNumbers" :key="index" class="mb-2">
                  <v-row no-gutters align="center">
                    <v-col cols="2">
                      <div class="country-code-box d-flex align-center justify-center"
                        style="border: 1px solid #B0B0B0; border-radius: 4px; background-color: #F5F5F5; height: 45px; font-size: 14px; padding: 0 8px;">
                        <span class="font-weight-medium">+94</span>
                      </div>
                    </v-col>

                    <v-col cols="7" class="px-2">
                      <v-text-field v-model="phone.number"
                        :label="index === 0 ? 'Primary Mobile Number *' : 'Secondary Mobile Number'"
                        :placeholder="index === 0 ? 'Type primary mobile number here' : 'Type secondary mobile number here'"
                        variant="outlined" density="comfortable" :rules="phoneRules" hide-details="auto"
                        :error-messages="fieldErrors[`phone_${index}`]" @input="restrictToNumbers(phone, $event)" />
                    </v-col>

                    <v-col cols="3" class="d-flex align-center">
                      <v-btn v-if="phoneNumbers.length > 1 && index > 0" variant="flat" class="text-none w-100"
                        style="height: 40px; color: #D32F2F; background-color: #FFEBEE; border: 1px solid #FFCDD2; font-size: 12px;"
                        @click="removePhoneNo(index)">
                        <v-icon start size="18">mdi-minus-circle-outline</v-icon>
                        REMOVE
                      </v-btn>
                    </v-col>
                  </v-row>
                </div>
              </v-col>

              <!-- Right Column -->
              <v-col cols="12" md="6">
                <v-text-field v-model="user.userCode" label="User Code *" placeholder="Type user code here"
                  variant="outlined" density="comfortable" class="mb-2" :rules="userCodeRules"
                  @input="checkUserCodeExists" @blur="checkUserCodeExists" :error-messages="userCodeErrorMessages"
                  maxlength="30" counter>
                </v-text-field>

                <v-select v-model="user.piyestraCompanies" :items="piyestraCompanies" label="Piyestra Company *"
                  placeholder="Select Piyestra company" variant="outlined" density="comfortable" class="mb-2" multiple
                  chips closable-chips :rules="[v => v && v.length > 0 || 'At least one Piyestra company is required']"
                  :error-messages="fieldErrors.piyestraCompanies">
                </v-select>

                <v-text-field v-model="user.nic" label="NIC *"
                  placeholder="Enter NIC (e.g., 200613500918 or 75785674673V)" variant="outlined" density="comfortable"
                  :rules="nicRules" :error-messages="fieldErrors.nic" @input="restrictNICInput"
                  hint="Format: 12 digits OR 9 digits followed by V/v/X/x" persistent-hint>
                </v-text-field>

                <v-select v-model="user.gender" :items="['Male', 'Female']" label="Gender *" placeholder="Select gender"
                  variant="outlined" density="comfortable" class="mt-2" :rules="[v => !!v || 'Gender is required']"
                  :error-messages="fieldErrors.gender">
                </v-select>

                <v-text-field v-model="user.address" label="Address" placeholder="No: 22/1, Ganapathi Road, Colombo 02"
                  variant="outlined" density="comfortable" class="mt-2" maxlength="255" counter>
                </v-text-field>

                <!-- Regional Code - Multi Select WITH ALL OPTION + CAPPED CHIPS (NO ALL CHIP) -->
                <v-select v-model="user.regionalCodes" label="Regional Code *" placeholder="Select regional code(s)"
                  :items="regionalCodeItemsWithAll" variant="outlined" density="comfortable" class="mt-2" multiple
                  :rules="[v => v && v.length > 0 || 'At least one Regional Code is required']"
                  :loading="loading.regionalCodes" :error="regionalCodeError"
                  :error-messages="regionalCodeError ? 'Failed to load regional codes' : fieldErrors.regionalCodes"
                  @update:model-value="onRegionalCodesChange">
                  <template v-slot:item="{ item }">
                    <v-list-item
                      :title="item.raw.title"
                      :value="item.raw.value"
                      @click.stop="onDropdownItemClick(item.raw, 'regional')">
                      <template v-slot:prepend>
                        <v-checkbox-btn
                          :model-value="isRegionalItemChecked(item.raw)"
                          @click.stop="onDropdownItemClick(item.raw, 'regional')"
                        ></v-checkbox-btn>
                      </template>
                    </v-list-item>
                  </template>
                  <template v-slot:selection="{ index }">
                    <template v-if="index === 0">
                      <v-chip v-for="code in visibleRegionalChips.slice(0, maxVisibleChips)" :key="code"
                        size="small" closable class="ma-1" @click:close="removeRegionalChip(code)">
                        {{ code }}
                      </v-chip>
                      <span v-if="visibleRegionalChips.length > maxVisibleChips"
                        class="text-caption text-grey-darken-1 ml-2">
                        +{{ visibleRegionalChips.length - maxVisibleChips }} more
                      </span>
                    </template>
                  </template>
                </v-select>

                <!-- Area Code - Multi Select WITH ALL OPTION + CAPPED CHIPS (NO ALL CHIP) -->
                <v-select v-model="user.areaCodes" label="Area Code *" placeholder="Select area code(s)"
                  :items="areaCodeItemsWithAll" variant="outlined" density="comfortable" class="mt-2" multiple
                  :rules="[v => v && v.length > 0 || 'At least one Area Code is required']"
                  :disabled="!user.regionalCodes || user.regionalCodes.length === 0 || loading.areaCodes"
                  :loading="loading.areaCodes" :error="areaCodeError"
                  :error-messages="areaCodeError ? 'Failed to load area codes' : fieldErrors.areaCodes" clearable
                  @update:model-value="onAreaCodesChange">
                  <template v-slot:item="{ item }">
                    <v-list-item
                      :title="item.raw.title"
                      :value="item.raw.value"
                      @click.stop="onDropdownItemClick(item.raw, 'area')">
                      <template v-slot:prepend>
                        <v-checkbox-btn
                          :model-value="isAreaItemChecked(item.raw)"
                          @click.stop="onDropdownItemClick(item.raw, 'area')"
                        ></v-checkbox-btn>
                      </template>
                    </v-list-item>
                  </template>
                  <template v-slot:selection="{ index }">
                    <template v-if="index === 0">
                      <v-chip v-for="code in visibleAreaChips.slice(0, maxVisibleChips)" :key="code"
                        size="small" closable class="ma-1" @click:close="removeAreaChip(code)">
                        {{ code }}
                      </v-chip>
                      <span v-if="visibleAreaChips.length > maxVisibleChips"
                        class="text-caption text-grey-darken-1 ml-2">
                        +{{ visibleAreaChips.length - maxVisibleChips }} more
                      </span>
                    </template>
                  </template>
                </v-select>

                <div class="mt-4">
                  <p class="text-subtitle-2 font-weight-bold mb-2">
                    User Image <span style="font-weight: normal; opacity: 0.6; font-size: 0.85rem;">(optional)</span>
                  </p>
                  <div class="drop-area pa-8 text-center"
                    style="border: 2px dashed #D1D1D1; background-color: #F8FAFF; border-radius: 8px; cursor: pointer;"
                    @click="triggerFileInput" @drop.prevent="onDrop" @dragover.prevent="onDragOver">
                    <input ref="fileInput" type="file" hidden accept="image/*" @change="onFileChange" />

                    <div v-if="previewImages.length === 0">
                      <v-icon size="48" color="grey-lighten-1">mdi-image-plus-outline</v-icon>
                      <div class="mt-2 text-body-2">
                        Drag your photo here <strong> OR </strong>
                        <span style="color: #156CF7">Browse from device</span>
                      </div>
                    </div>

                    <div v-else class="position-relative d-inline-block">
                      <v-img :src="previewImages[0]" width="150" height="100" cover rounded></v-img>
                      <v-btn icon="mdi-close" size="x-small" color="red" class="position-absolute"
                        style="top: -10px; right: -10px" @click.stop="removeImage"></v-btn>
                    </div>
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions class="pa-6">
          <v-spacer></v-spacer>
          <v-btn v-if="permissions.write" color="#800080" variant="flat" size="large"
            class="px-8 text-none font-weight-bold" style="color: #ffffff; border: 1px solid #800080"
            @click="registerUser" :loading="centerLoading" :disabled="isCurrentCategoryFull">
            REGISTER USER
          </v-btn>
          <v-btn variant="outlined" size="large" class="px-8 ml-4 text-none font-weight-bold" @click="cancel">
            CANCEL
          </v-btn>
        </v-card-actions>
      </v-card>

      <v-snackbar v-model="snackAlert.snackbar" :color="snackAlert.snackColor" timeout="3000" location="top right">
        {{ snackAlert.snackMessage }}
      </v-snackbar>
    </v-container>
  </v-app>
</template>

<script>
import axios from 'axios';
import { getPermissions } from "@/plugins/permissions";

export default {
  name: 'UserRegistration',
  data() {
    return {
      permissions: { read: false, write: false, delete: false },
      centerLoading: false,
      loading: {
        userTypes: false,
        regionalCodes: false,
        areaCodes: false
      },
      regionalCodeError: false,
      areaCodeError: false,
      geofenceEnabled: 'yes',
      showPassword: false,
      showConfirmPassword: false,
      confirmPassword: '',
      piyestraCompanies: [
        'Piyestra Furniture (Pvt) Ltd',
        'D.R.Industries (Pvt) Ltd',
        'D.R.Home Appliances (Pvt) Ltd'
      ],

      // ===== USER LIMITS (must match the User List dashboard) =====
      maxUsers: 300,
      maxWebUsers: 100,
      maxMobileUsers: 200,

      user: {
        fullName: '',
        userName: '',
        userCode: '',
        emailAddress: '',
        password: '',
        userCategory: null,
        userType: '',
        piyestraCompanies: [],
        nic: '',
        gender: '',
        address: '',
        regionalCodes: [],
        areaCodes: [],
        image: []
      },
      fieldErrors: {},
      phoneNumbers: [{ number: '' }],
      previewImages: [],
      userCategories: [
        { name: 'Web User', value: 'Web User' },
        { name: 'Mobile User', value: 'Mobile User' },
        { name: 'Web User, Mobile User', value: 'Web User,Mobile User' }
      ],
      userTypes: [],
      regionalCodes: [],
      selectedAreaCodes: [],
      errorMessage: '',
      snackAlert: {
        snackbar: false,
        snackColor: "",
        snackMessage: "",
      },
      existingUsers: [],
      ALL_VALUE: 'ALL',
      maxVisibleChips: 4,

      phoneRules: [
        v => !!v || "Mobile Number is required",
        v => /^\d{9}$/.test(v) || "Must be 9 digits (without country code)"
      ],
      emailRules: [
        v => !v || /.+@.+\..+/.test(v) || 'Invalid Email'
      ],
      passwordRules: [
        v => !!v || 'Password is required',
        v => (v && v.length >= 8) || 'Password must be at least 8 characters',
        v => (v && v.length <= 50) || 'Password must be less than 50 characters',
        v => (v && /[A-Z]/.test(v)) || 'Password must contain at least one uppercase letter',
        v => (v && /[a-z]/.test(v)) || 'Password must contain at least one lowercase letter',
        v => (v && /[0-9]/.test(v)) || 'Password must contain at least one number',
        v => (v && /[!@#$%^&*(),.?":{}|<>]/.test(v)) || 'Password must contain at least one special character'
      ],
      nicRules: [
        v => !!v || 'NIC is required',
        v => {
          const type1Pattern = /^\d{12}$/;
          const type2Pattern = /^\d{9}[vVxX]$/;
          if (type1Pattern.test(v)) return true;
          if (type2Pattern.test(v)) return true;
          return 'Invalid NIC format. Use 12 digits OR 9 digits followed by V/v/X/x';
        }
      ],
      userRolesCheckDetails: { name: [] },
      initialDataLoaded: false,
      loggedInUserId: null,
      isLoggingOut: false,
      tokenCheckInterval: null
    };
  },
  computed: {
    confirmPasswordRules() {
      return [
        v => !!v || 'Please confirm your password',
        v => v === this.user.password || 'Passwords do not match'
      ];
    },

    usernameErrorMessages() {
      return this.fieldErrors.userName ? [this.fieldErrors.userName] : [];
    },

    userCodeErrorMessages() {
      return this.fieldErrors.userCode ? [this.fieldErrors.userCode] : [];
    },

    emailErrorMessages() {
      return this.fieldErrors.emailAddress ? [this.fieldErrors.emailAddress] : [];
    },

    // ===== FIXED: use this.userTypes (component-level), not this.user.userTypes =====
    // Also normalizes category strings so "Web User,Mobile User" matches "Web User, Mobile User"
    filteredUserTypes() {
      if (!this.user.userCategory || !this.userTypes?.length) return [];

      // Split the selected category into individual tokens
      const selectedCategories = String(this.user.userCategory)
        .split(',')
        .map(c => c.trim())
        .filter(Boolean);

      return this.userTypes.filter(type => {
        if (this.isSuperAdminRole(type.role_type)) return false;

        // Split the role's category the same way
        const roleCategories = String(type.category || '')
          .split(',')
          .map(c => c.trim())
          .filter(Boolean);

        // Every selected category must be present in the role's categories
        return selectedCategories.every(sel => roleCategories.includes(sel));
      });
    },

    regionalCodeStrings() {
      return this.regionalCodes.map(item => item.region_code);
    },

    regionalCodeItemsWithAll() {
      return [
        { title: 'ALL', value: this.ALL_VALUE },
        ...this.regionalCodeStrings.map(code => ({ title: code, value: code }))
      ];
    },

    areaCodeItemsWithAll() {
      return [
        { title: 'ALL', value: this.ALL_VALUE },
        ...this.selectedAreaCodes.map(code => ({ title: code, value: code }))
      ];
    },

    visibleRegionalChips() {
      return this.user.regionalCodes.filter(v => v !== this.ALL_VALUE);
    },

    visibleAreaChips() {
      return this.user.areaCodes.filter(v => v !== this.ALL_VALUE);
    },

    fullNameRules() {
      return [
        v => !!v || 'Full Name is required',
        v => (v && v.length <= 100) || 'Full Name must be less than 100 characters',
        v => (v && v.trim().length > 0) || 'Full Name cannot be empty or only spaces',
        v => !v || /^[a-zA-Z\s\-.'\u0600-\u06FF\u4e00-\u9fa5]+$/.test(v) || 'Full Name contains invalid characters'
      ];
    },

    userNameRules() {
      return [
        v => !!v || 'User Name is required',
        v => (v && v.length <= 32) || 'User Name must be less than 32 characters',
        v => (v && v.length >= 3) || 'User Name must be at least 3 characters',
        v => (v && /^[a-zA-Z0-9_ ]+$/.test(v)) || 'User Name can only contain letters, numbers, spaces, and underscores',
        v => (v && v.trim().length > 0) || 'User Name cannot be empty or only spaces'
      ];
    },

    userCodeRules() {
      return [
        v => !!v || 'User Code is required',
        v => (v && v.length <= 30) || 'User Code must be less than 30 characters',
        v => (v && v.length >= 2) || 'User Code must be at least 2 characters',
        v => (v && /^[a-zA-Z0-9\-_]+$/.test(v)) || 'User Code can only contain letters, numbers, hyphens, and underscores',
        v => (v && v.trim().length > 0) || 'User Code cannot be empty or only spaces'
      ];
    },

    showGeofenceOption() {
      if (!this.user.userType) return false;
      const selectedRole = this.userTypes.find(type => Number(type.id) === Number(this.user.userType));
      return selectedRole && (selectedRole.role_type === 'SALES REP' || selectedRole.role_type === 'AREA MANAGER');
    },

    // ===== USER LIMIT COUNTING =====

    // Only count ACTIVE users (status === 'Activate'), matching the User List dashboard
    activeExistingUsers() {
      return this.existingUsers.filter(u => u.status === 'Activate');
    },

    totalUserCount() {
      return this.activeExistingUsers.length;
    },

    webUserCount() {
      return this.activeExistingUsers.filter(u =>
        this.hasCategory(u.user_category, 'Web User')
      ).length;
    },

    mobileUserCount() {
      return this.activeExistingUsers.filter(u =>
        this.hasCategory(u.user_category, 'Mobile User')
      ).length;
    },

    // Is the currently selected category already at/over its max?
    isCurrentCategoryFull() {
      if (!this.user.userCategory) return false;

      // Total cap check
      if (this.totalUserCount >= this.maxUsers) return true;

      // Category-specific check
      if (this.user.userCategory === 'Web User') {
        return this.webUserCount >= this.maxWebUsers;
      }
      if (this.user.userCategory === 'Mobile User') {
        return this.mobileUserCount >= this.maxMobileUsers;
      }
      // Both categories — a new "both" user consumes one Web AND one Mobile slot.
      // Block if either would exceed.
      if (this.user.userCategory === 'Web User,Mobile User') {
        return this.webUserCount >= this.maxWebUsers ||
               this.mobileUserCount >= this.maxMobileUsers;
      }
      return false;
    },

    limitWarningMessage() {
      if (!this.user.userCategory) return '';

      if (this.totalUserCount >= this.maxUsers) {
        return `Maximum total users reached (${this.totalUserCount} / ${this.maxUsers}). Cannot create more users.`;
      }

      if (this.user.userCategory === 'Web User') {
        return `Maximum Web Users reached (${this.webUserCount} / ${this.maxWebUsers}). Cannot create more Web Users.`;
      }
      if (this.user.userCategory === 'Mobile User') {
        return `Maximum Mobile Users reached (${this.mobileUserCount} / ${this.maxMobileUsers}). Cannot create more Mobile Users.`;
      }
      if (this.user.userCategory === 'Web User,Mobile User') {
        if (this.webUserCount >= this.maxWebUsers) {
          return `Maximum Web Users reached (${this.webUserCount} / ${this.maxWebUsers}). Cannot create a Web + Mobile user.`;
        }
        if (this.mobileUserCount >= this.maxMobileUsers) {
          return `Maximum Mobile Users reached (${this.mobileUserCount} / ${this.maxMobileUsers}). Cannot create a Web + Mobile user.`;
        }
      }
      return '';
    }
  },
  async mounted() {
    this.permissions = getPermissions(3, "User Registration");
    if (!this.permissions.read) {
      this.$router.push('/dashboard');
    }
    this.loggedInUserId = localStorage.getItem("userId");
    const token = localStorage.getItem("token");

    if (!token || !this.loggedInUserId) {
      this.showSnackbar('Please login to continue', 'warning');
      setTimeout(() => {
        this.$router.push('/login');
      }, 1500);
      return;
    }

    if (!this.isTokenValid(token)) {
      this.showSnackbar('Your session has expired. Please login again.', 'warning');
      setTimeout(() => {
        this.$router.push('/login');
      }, 1500);
      return;
    }

    this.tokenCheckInterval = setInterval(() => {
      const currentToken = localStorage.getItem("token");
      if (!this.isTokenValid(currentToken)) {
        this.handleUnauthorized();
      }
    }, 300000);

    await this.fetchExistingUsers();
    await this.fetchRegionalCodes();

    this.initialDataLoaded = true;
  },

  beforeUnmount() {
    if (this.previewImages.length > 0) {
      this.previewImages.forEach(url => URL.revokeObjectURL(url));
    }
    if (this.tokenCheckInterval) {
      clearInterval(this.tokenCheckInterval);
    }
  },

  methods: {
    // ===== CATEGORY MATCHING HELPER (same as User List page) =====
    // Handles "Web User,Mobile User" and "Web User, Mobile User"
    hasCategory(userCategory, targetCategory) {
      if (!userCategory) return false;
      const categories = String(userCategory)
        .split(',')
        .map(c => c.trim());
      return categories.includes(targetCategory);
    },

    // ===== USER LIMIT CHECK =====
    // Returns { allowed: boolean, message: string }
    checkUserLimit() {
      if (!this.user.userCategory) {
        return { allowed: true, message: '' };
      }

      // Total cap
      if (this.totalUserCount >= this.maxUsers) {
        return {
          allowed: false,
          message: `Maximum total users reached (${this.totalUserCount} / ${this.maxUsers}). Cannot create more users.`
        };
      }

      // Category-specific cap
      if (this.user.userCategory === 'Web User') {
        if (this.webUserCount >= this.maxWebUsers) {
          return {
            allowed: false,
            message: `Maximum Web Users reached (${this.webUserCount} / ${this.maxWebUsers}). Cannot create more Web Users.`
          };
        }
      } else if (this.user.userCategory === 'Mobile User') {
        if (this.mobileUserCount >= this.maxMobileUsers) {
          return {
            allowed: false,
            message: `Maximum Mobile Users reached (${this.mobileUserCount} / ${this.maxMobileUsers}). Cannot create more Mobile Users.`
          };
        }
      } else if (this.user.userCategory === 'Web User,Mobile User') {
        if (this.webUserCount >= this.maxWebUsers) {
          return {
            allowed: false,
            message: `Maximum Web Users reached (${this.webUserCount} / ${this.maxWebUsers}). Cannot create a Web + Mobile user.`
          };
        }
        if (this.mobileUserCount >= this.maxMobileUsers) {
          return {
            allowed: false,
            message: `Maximum Mobile Users reached (${this.mobileUserCount} / ${this.maxMobileUsers}). Cannot create a Web + Mobile user.`
          };
        }
      }

      return { allowed: true, message: '' };
    },

    togglePasswordVisibility() {
      this.showPassword = !this.showPassword;
    },
    toggleConfirmPasswordVisibility() {
      this.showConfirmPassword = !this.showConfirmPassword;
    },

    checkUsernameExists() {
      if (!this.user.userName || this.user.userName.length < 3) {
        this.fieldErrors.userName = '';
        return;
      }
      const exists = this.existingUsers.some(user =>
        user.user_name && user.user_name.toLowerCase() === this.user.userName.toLowerCase()
      );
      this.fieldErrors.userName = exists ? 'This username already exists' : '';
    },

    checkUserCodeExists() {
      if (!this.user.userCode || this.user.userCode.length < 2) {
        this.fieldErrors.userCode = '';
        return;
      }
      const exists = this.existingUsers.some(user =>
        user.user_code && user.user_code.toLowerCase() === this.user.userCode.toLowerCase()
      );
      this.fieldErrors.userCode = exists ? 'This user code already exists' : '';
    },

    checkEmailExists() {
      if (!this.user.emailAddress) {
        this.fieldErrors.emailAddress = '';
        return;
      }
      if (!/.+@.+\..+/.test(this.user.emailAddress)) {
        this.fieldErrors.emailAddress = '';
        return;
      }
      const exists = this.existingUsers.some(user =>
        user.email && user.email.toLowerCase() === this.user.emailAddress.toLowerCase()
      );
      this.fieldErrors.emailAddress = exists ? 'This email address already exists' : '';
    },

    restrictToNumbers(phone, event) {
      const cleaned = event.target.value.replace(/\D/g, '');
      phone.number = cleaned.slice(0, 9);
    },

    restrictNICInput(event) {
      let value = event.target.value;
      value = value.toUpperCase();
      let cleaned = '';
      let hasLetter = false;

      for (let i = 0; i < value.length; i++) {
        const char = value[i];
        if (/[0-9]/.test(char)) {
          cleaned += char;
        } else if (/[VX]/.test(char) && !hasLetter && cleaned.length === 9) {
          cleaned += char;
          hasLetter = true;
        } else if (/[VX]/.test(char) && !hasLetter && cleaned.length === 0) {
          continue;
        } else if (/[VX]/.test(char) && hasLetter) {
          continue;
        }
      }

      if (cleaned.length > 12) {
        cleaned = cleaned.slice(0, 12);
      }

      this.user.nic = cleaned;
      if (event.target) {
        event.target.value = cleaned;
      }
    },

    isTokenValid(token) {
      if (!token) return false;
      try {
        const parts = token.split('.');
        if (parts.length !== 3) return false;
        const payload = JSON.parse(atob(parts[1]));
        const expirationTime = payload.exp * 1000;
        const currentTime = Date.now();
        return expirationTime > currentTime;
      } catch (e) {
        console.error('Token validation error:', e);
        return false;
      }
    },

    getAuthHeaders() {
      const token = localStorage.getItem("token");
      if (!token) return null;
      const cleanToken = token.trim().replace(/^"(.*)"$/, '$1');
      return {
        'Authorization': `Bearer ${cleanToken}`,
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      };
    },

    getApiUserId() {
      if (this.loggedInUserId) return this.loggedInUserId;
      const userId = localStorage.getItem("userId");
      if (!userId) return null;
      this.loggedInUserId = userId;
      return userId;
    },

    handleUnauthorized() {
      if (this.isLoggingOut) return;
      this.isLoggingOut = true;
      this.showSnackbar('Your session has expired. Please login again.', 'error');
      localStorage.removeItem("token");
      localStorage.removeItem("userId");
      localStorage.removeItem("userDetails");
      delete axios.defaults.headers.common['Authorization'];
      if (this.tokenCheckInterval) clearInterval(this.tokenCheckInterval);
      setTimeout(() => {
        this.$router.push('/login');
      }, 1500);
    },

    async fetchExistingUsers() {
      const headers = this.getAuthHeaders();
      const userId = this.getApiUserId();
      if (!headers || !userId) return;
      try {
        const formData = new FormData();
        formData.append("userId", userId);
        const res = await axios.post(`/api/createdUserList`, formData, { headers });
        if (res.data?.success && Array.isArray(res.data.data)) {
          this.existingUsers = res.data.data;
          this.userRolesCheckDetails.name = res.data.data.map(i => i.user_name);
        }
      } catch (err) {
        console.error('Error fetching existing users:', err);
        if (err.response?.status === 401) this.handleUnauthorized();
      }
    },

    async fetchUserRoles() {
      if (!this.user.userCategory) return;
      this.loading.userTypes = true;
      const headers = this.getAuthHeaders();
      const userId = this.getApiUserId();
      if (!headers || !userId) {
        this.loading.userTypes = false;
        this.showSnackbar('User ID not found', 'error');
        return;
      }
      try {
        const formData = new FormData();
        formData.append("userId", userId);
        const res = await axios.post(`/api/getUserRolesType`, formData, { headers });

        // Debug log — remove once verified
        console.log('getUserRolesType response:', res.data);

        this.userTypes = (res.data && Array.isArray(res.data.data)) ? res.data.data : [];

        if (this.userTypes.length === 0) {
          this.showSnackbar('No user roles available for this category', 'info');
        }
      } catch (err) {
        console.error('Error fetching user roles:', err);
        this.showSnackbar('Could not load user roles', 'warning');
        this.userTypes = [];
      } finally {
        this.loading.userTypes = false;
      }
    },

    async fetchRegionalCodes() {
      this.loading.regionalCodes = true;
      this.regionalCodeError = false;
      const headers = this.getAuthHeaders();
      const userId = this.getApiUserId();
      if (!headers || !userId) {
        this.regionalCodes = [];
        this.loading.regionalCodes = false;
        return;
      }
      try {
        const res = await axios.get(`/api/region-code-list?userId=${userId}`, { headers });
        if (res.data?.success === true && Array.isArray(res.data.data)) {
          this.regionalCodes = res.data.data.map(code => ({
            region_name: code,
            region_code: code
          }));
        } else {
          this.regionalCodes = [];
        }
      } catch (err) {
        console.error('Error fetching regional codes:', err);
        this.regionalCodeError = true;
        this.regionalCodes = [];
        this.showSnackbar('Could not load regional codes', 'info');
      } finally {
        this.loading.regionalCodes = false;
      }
    },

    async fetchAreaCodesForRegions(regionCodes) {
      const realRegionCodes = (regionCodes || []).filter(c => c !== this.ALL_VALUE);

      if (!realRegionCodes || realRegionCodes.length === 0) {
        this.selectedAreaCodes = [];
        return;
      }
      this.loading.areaCodes = true;
      this.areaCodeError = false;
      this.selectedAreaCodes = [];
      const headers = this.getAuthHeaders();
      const userId = this.getApiUserId();
      if (!headers || !userId) {
        this.loading.areaCodes = false;
        this.showSnackbar('Authentication error. Please login again.', 'error');
        return;
      }
      try {
        const token = localStorage.getItem("token");
        if (!this.isTokenValid(token)) {
          this.handleUnauthorized();
          return;
        }
        const allAreaCodes = new Set();
        for (const regionCode of realRegionCodes) {
          try {
            const res = await axios.get(`/api/area-code-by-region`, {
              headers,
              params: { userId: userId, region_code: regionCode }
            });
            if (res.data?.success === true && Array.isArray(res.data.data)) {
              res.data.data.forEach(code => allAreaCodes.add(code));
            }
          } catch (regionErr) {
            console.error(`Error fetching area codes for region ${regionCode}:`, regionErr);
          }
        }
        this.selectedAreaCodes = Array.from(allAreaCodes);
        if (this.user.areaCodes && this.user.areaCodes.length > 0) {
          this.user.areaCodes = this.user.areaCodes.filter(code =>
            code === this.ALL_VALUE || this.selectedAreaCodes.includes(code)
          );
        }
        if (this.selectedAreaCodes.length === 0) {
          this.showSnackbar('No area codes found for selected region(s)', 'info');
        }
      } catch (err) {
        console.error('Error fetching area codes:', err);
        this.areaCodeError = true;
        this.selectedAreaCodes = [];
        if (err.response?.status === 401) this.handleUnauthorized();
      } finally {
        this.loading.areaCodes = false;
      }
    },

    onUserCategoryChange() {
      this.user.userType = '';
      this.fetchUserRoles();

      // ===== Immediate limit feedback =====
      this.$nextTick(() => {
        const { allowed, message } = this.checkUserLimit();
        if (!allowed) {
          this.showSnackbar(message, 'warning');
        }
      });
    },

    onUserTypeChange() {
      this.geofenceEnabled = 'yes';
    },

    isRegionalItemChecked(item) {
      if (item.value === this.ALL_VALUE) {
        return this.user.regionalCodes.includes(this.ALL_VALUE);
      }
      return this.user.regionalCodes.includes(item.value);
    },

    isAreaItemChecked(item) {
      if (item.value === this.ALL_VALUE) {
        return this.user.areaCodes.includes(this.ALL_VALUE);
      }
      return this.user.areaCodes.includes(item.value);
    },

    onDropdownItemClick(item, kind) {
      const allCodes = kind === 'regional' ? this.regionalCodeStrings : this.selectedAreaCodes;
      const current = kind === 'regional' ? [...this.user.regionalCodes] : [...this.user.areaCodes];
      const allValue = this.ALL_VALUE;

      let result;

      if (item.value === allValue) {
        const allRealSelected = allCodes.length > 0 &&
          allCodes.every(c => current.includes(c));
        result = allRealSelected ? [] : [allValue, ...allCodes];
      } else {
        const isChecked = current.includes(item.value);

        if (isChecked) {
          result = current.filter(v => v !== item.value && v !== allValue);
        } else {
          result = [...current.filter(v => v !== allValue), item.value];

          if (allCodes.length > 0 && allCodes.every(c => result.includes(c))) {
            result = [allValue, ...result];
          }
        }
      }

      if (kind === 'regional') {
        this.user.regionalCodes = result;
        this.user.areaCodes = [];
        const realRegions = result.filter(v => v !== allValue);
        if (realRegions.length > 0) {
          this.fetchAreaCodesForRegions(realRegions);
        } else {
          this.selectedAreaCodes = [];
        }
      } else {
        this.user.areaCodes = result;
      }
    },

    onRegionalCodesChange() {
      const realRegions = this.user.regionalCodes.filter(v => v !== this.ALL_VALUE);
      this.user.areaCodes = [];
      if (realRegions.length > 0) {
        this.fetchAreaCodesForRegions(realRegions);
      } else {
        this.selectedAreaCodes = [];
      }
    },

    onAreaCodesChange() {
      // nothing extra needed
    },

    removeRegionalChip(value) {
      const allValue = this.ALL_VALUE;
      let result;

      if (value === allValue) {
        result = [];
      } else {
        result = this.user.regionalCodes.filter(v => v !== value);
        const realCount = result.filter(v => v !== allValue).length;
        if (result.includes(allValue) && realCount < this.regionalCodeStrings.length) {
          result = result.filter(v => v !== allValue);
        }
      }

      this.user.regionalCodes = result;
      this.user.areaCodes = [];
      const realRegions = result.filter(v => v !== allValue);
      if (realRegions.length > 0) {
        this.fetchAreaCodesForRegions(realRegions);
      } else {
        this.selectedAreaCodes = [];
      }
    },

    removeAreaChip(value) {
      const allValue = this.ALL_VALUE;
      let result;

      if (value === allValue) {
        result = [];
      } else {
        result = this.user.areaCodes.filter(v => v !== value);
        const realCount = result.filter(v => v !== allValue).length;
        if (result.includes(allValue) && realCount < this.selectedAreaCodes.length) {
          result = result.filter(v => v !== allValue);
        }
      }

      this.user.areaCodes = result;
    },

    addPhoneNo() {
      if (this.phoneNumbers.length < 2) {
        this.phoneNumbers.push({ number: '' });
      } else {
        this.showSnackbar("Maximum 2 phone numbers allowed", "warning");
      }
    },

    removePhoneNo(index) {
      this.phoneNumbers.splice(index, 1);
    },

    triggerFileInput() {
      this.$refs.fileInput.click();
    },

    onFileChange(e) {
      const file = e.target.files[0];
      if (file) this.handleFiles(file);
    },

    onDrop(e) {
      const file = e.dataTransfer.files[0];
      if (file) this.handleFiles(file);
    },

    onDragOver(e) {
      e.preventDefault();
    },

    handleFiles(file) {
      if (!file.type.startsWith('image/')) {
        this.showSnackbar('Please select an image file', 'warning');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        this.showSnackbar('Image size should be less than 5MB', 'warning');
        return;
      }
      this.user.image = [file];
      this.previewImages = [URL.createObjectURL(file)];
    },

    removeImage() {
      this.user.image = [];
      this.previewImages = [];
    },

    validateForm() {
      this.checkUsernameExists();
      this.checkUserCodeExists();
      this.checkEmailExists();

      let isValid = true;

      if (!this.user.fullName) {
        this.fieldErrors.fullName = 'Full Name is required';
        isValid = false;
      } else if (this.user.fullName.length > 100) {
        this.fieldErrors.fullName = 'Full Name must be less than 100 characters';
        isValid = false;
      } else if (!/^[a-zA-Z\s\-.'\u0600-\u06FF\u4e00-\u9fa5]+$/.test(this.user.fullName)) {
        this.fieldErrors.fullName = 'Full Name contains invalid characters';
        isValid = false;
      } else {
        this.fieldErrors.fullName = '';
      }

      if (!this.user.userName) {
        this.fieldErrors.userName = 'User Name is required';
        isValid = false;
      } else if (this.user.userName.length > 32) {
        this.fieldErrors.userName = 'User Name must be less than 32 characters';
        isValid = false;
      } else if (this.user.userName.length < 3) {
        this.fieldErrors.userName = 'User Name must be at least 3 characters';
        isValid = false;
      } else if (!/^[a-zA-Z0-9_ ]+$/.test(this.user.userName)) {
        this.fieldErrors.userName = 'User Name can only contain letters, numbers, spaces, and underscores';
        isValid = false;
      } else if (this.fieldErrors.userName === 'This username already exists') {
        isValid = false;
      } else {
        this.fieldErrors.userName = '';
      }

      if (!this.user.userCode) {
        this.fieldErrors.userCode = 'User Code is required';
        isValid = false;
      } else if (this.user.userCode.length > 30) {
        this.fieldErrors.userCode = 'User Code must be less than 30 characters';
        isValid = false;
      } else if (this.user.userCode.length < 2) {
        this.fieldErrors.userCode = 'User Code must be at least 2 characters';
        isValid = false;
      } else if (!/^[a-zA-Z0-9\-_]+$/.test(this.user.userCode)) {
        this.fieldErrors.userCode = 'User Code can only contain letters, numbers, hyphens, and underscores';
        isValid = false;
      } else if (this.fieldErrors.userCode === 'This user code already exists') {
        isValid = false;
      } else {
        this.fieldErrors.userCode = '';
      }

      if (this.user.emailAddress) {
        if (this.user.emailAddress.length > 100) {
          this.fieldErrors.emailAddress = 'Email must be less than 100 characters';
          isValid = false;
        } else if (!/.+@.+\..+/.test(this.user.emailAddress)) {
          this.fieldErrors.emailAddress = 'Invalid Email';
          isValid = false;
        } else if (this.fieldErrors.emailAddress === 'This email address already exists') {
          isValid = false;
        } else {
          this.fieldErrors.emailAddress = '';
        }
      } else {
        this.fieldErrors.emailAddress = '';
      }

      if (!this.user.password) {
        this.fieldErrors.password = 'Password is required';
        isValid = false;
      } else if (this.user.password.length < 8) {
        this.fieldErrors.password = 'Password must be at least 8 characters';
        isValid = false;
      } else if (this.user.password.length > 50) {
        this.fieldErrors.password = 'Password must be less than 50 characters';
        isValid = false;
      } else if (!/[A-Z]/.test(this.user.password)) {
        this.fieldErrors.password = 'Password must contain at least one uppercase letter';
        isValid = false;
      } else if (!/[a-z]/.test(this.user.password)) {
        this.fieldErrors.password = 'Password must contain at least one lowercase letter';
        isValid = false;
      } else if (!/[0-9]/.test(this.user.password)) {
        this.fieldErrors.password = 'Password must contain at least one number';
        isValid = false;
      } else if (!/[!@#$%^&*(),.?":{}|<>]/.test(this.user.password)) {
        this.fieldErrors.password = 'Password must contain at least one special character';
        isValid = false;
      } else {
        this.fieldErrors.password = '';
      }

      if (!this.confirmPassword) {
        this.fieldErrors.confirmPassword = 'Please confirm your password';
        isValid = false;
      } else if (this.confirmPassword !== this.user.password) {
        this.fieldErrors.confirmPassword = 'Passwords do not match';
        isValid = false;
      } else {
        this.fieldErrors.confirmPassword = '';
      }

      if (!this.user.userCategory) {
        this.fieldErrors.userCategory = 'User Category is required';
        isValid = false;
      } else {
        this.fieldErrors.userCategory = '';
      }

      if (!this.user.userType) {
        this.fieldErrors.userType = 'User Role is required';
        isValid = false;
      } else {
        this.fieldErrors.userType = '';
      }

      if (!this.user.piyestraCompanies || this.user.piyestraCompanies.length === 0) {
        this.fieldErrors.piyestraCompanies = 'At least one Piyestra company is required';
        isValid = false;
      } else {
        this.fieldErrors.piyestraCompanies = '';
      }

      if (!this.user.nic) {
        this.fieldErrors.nic = 'NIC is required';
        isValid = false;
      } else {
        const type1Pattern = /^\d{12}$/;
        const type2Pattern = /^\d{9}[vVxX]$/;
        if (!type1Pattern.test(this.user.nic) && !type2Pattern.test(this.user.nic)) {
          this.fieldErrors.nic = 'Invalid NIC format. Use 12 digits OR 9 digits followed by V/v/X/x';
          isValid = false;
        } else {
          this.fieldErrors.nic = '';
        }
      }

      if (!this.user.gender) {
        this.fieldErrors.gender = 'Gender is required';
        isValid = false;
      } else {
        this.fieldErrors.gender = '';
      }

      if (!this.user.regionalCodes || this.user.regionalCodes.length === 0) {
        this.fieldErrors.regionalCodes = 'At least one Regional Code is required';
        isValid = false;
      } else {
        this.fieldErrors.regionalCodes = '';
      }

      if (!this.user.areaCodes || this.user.areaCodes.length === 0) {
        this.fieldErrors.areaCodes = 'At least one Area Code is required';
        isValid = false;
      } else {
        this.fieldErrors.areaCodes = '';
      }

      this.phoneNumbers.forEach((phone, index) => {
        if (!phone.number) {
          this.fieldErrors[`phone_${index}`] = index === 0 ? 'Primary Mobile Number is required' : 'Secondary Mobile Number is required';
          isValid = false;
        } else if (!/^\d{9}$/.test(phone.number)) {
          this.fieldErrors[`phone_${index}`] = 'Must be 9 digits';
          isValid = false;
        } else if (index === 1 && phone.number === this.phoneNumbers[0].number) {
          this.fieldErrors[`phone_${index}`] = 'Secondary number must be different from primary number';
          isValid = false;
        } else {
          this.fieldErrors[`phone_${index}`] = '';
        }
      });

      if (this.errorMessage) isValid = false;
      return isValid;
    },

    async registerUser() {
      const headers = this.getAuthHeaders();
      const userId = this.getApiUserId();

      if (!headers || !userId) {
        this.showSnackbar('Authentication error. Please login again.', 'error');
        return;
      }

      const token = localStorage.getItem("token");
      if (!this.isTokenValid(token)) {
        this.handleUnauthorized();
        return;
      }

      // ===== USER LIMIT CHECK =====
      const { allowed, message } = this.checkUserLimit();
      if (!allowed) {
        this.showSnackbar(message, 'warning');
        return;
      }

      if (!this.validateForm()) {
        this.showSnackbar('Please fill in all required fields correctly.', 'error');
        return;
      }

      const selectedRole = this.userTypes.find(type => Number(type.id) === Number(this.user.userType));
      if (selectedRole && this.isSuperAdminRole(selectedRole.role_type)) {
        this.showSnackbar('Super admin role cannot be assigned from user registration.', 'warning');
        return;
      }

      this.centerLoading = true;

      try {
        const phoneNumbersWithType = this.phoneNumbers.map((phone, index) => ({
          countryCode: '+94',
          number: phone.number,
          type: index === 0 ? 'primary' : 'secondary'
        }));

        const formData = new FormData();
        formData.append('fullName', this.user.fullName);
        formData.append('userName', this.user.userName);
        formData.append('userCode', this.user.userCode);
        formData.append('emailAddress', this.user.emailAddress || '');
        formData.append('password', this.user.password);
        formData.append('userType', parseInt(this.user.userType));
        formData.append('nic', this.user.nic.toUpperCase());
        formData.append('gender', this.user.gender);
        formData.append('mobileNumbers', JSON.stringify(phoneNumbersWithType));
        formData.append('piyestraCompanies', JSON.stringify(this.user.piyestraCompanies));
        formData.append('regionalCodes', JSON.stringify(this.user.regionalCodes.filter(c => c !== this.ALL_VALUE)));
        formData.append('areaCodes', JSON.stringify(this.user.areaCodes.filter(c => c !== this.ALL_VALUE)));
        formData.append('geofence', this.geofenceEnabled);
        formData.append('userCategory', this.user.userCategory);
        formData.append('address', this.user.address || '');
        formData.append('createdBy', parseInt(userId));

        if (this.user.image && this.user.image.length > 0 && this.user.image[0] instanceof File) {
          formData.append('image', this.user.image[0]);
        }

        await this.submitRegistration(formData, headers);
      } catch (error) {
        console.error('Registration error:', error);
        this.centerLoading = false;
        this.showSnackbar('An error occurred during registration.', 'error');
      }
    },

    async submitRegistration(formData, headers) {
      try {
        const uploadHeaders = { ...headers };
        delete uploadHeaders['Content-Type'];

        const response = await axios.post(`/api/createUser`, formData, {
          headers: uploadHeaders
        });

        if (response.data?.success && (response.status === 201 || response.status === 200)) {
          this.showSnackbar("User registered successfully", "success");
          setTimeout(() => {
            this.cancel();
            this.$router.push('/users-list');
          }, 1500);
        } else {
          this.showSnackbar(response.data?.message || "Registration failed", "error");
        }
      } catch (e) {
        console.error('Submit registration error:', e);
        if (e.response?.status === 401) {
          this.handleUnauthorized();
        } else if (e.response?.status === 422 && e.response?.data?.errors) {
          const errors = e.response.data.errors;
          let errorMessage = '';

          if (errors.emailAddress) {
            this.fieldErrors.emailAddress = errors.emailAddress[0];
            errorMessage += errors.emailAddress[0] + '\n';
          }
          if (errors.userName) {
            this.fieldErrors.userName = errors.userName[0];
            errorMessage += errors.userName[0] + '\n';
          }
          if (errors.userCode) {
            this.fieldErrors.userCode = errors.userCode[0];
            errorMessage += errors.userCode[0] + '\n';
          }
          if (errors.nic) {
            this.fieldErrors.nic = errors.nic[0];
            errorMessage += errors.nic[0] + '\n';
          }

          if (errorMessage) {
            this.showSnackbar(errorMessage, 'error');
          } else {
            this.showSnackbar(e.response?.data?.message || "Validation failed", "error");
          }
        } else {
          this.showSnackbar(e.response?.data?.message || "Registration failed. Please try again.", "error");
        }
      } finally {
        this.centerLoading = false;
      }
    },

    cancel() {
      this.$refs.form?.reset();
      this.phoneNumbers = [{ number: '' }];
      this.previewImages = [];
      this.geofenceEnabled = 'yes';
      this.user = {
        fullName: '',
        userName: '',
        userCode: '',
        emailAddress: '',
        password: '',
        userCategory: null,
        userType: '',
        piyestraCompanies: [],
        nic: '',
        gender: '',
        address: '',
        regionalCodes: [],
        areaCodes: [],
        image: []
      };
      this.confirmPassword = '';
      this.selectedAreaCodes = [];
      this.errorMessage = '';
      this.regionalCodeError = false;
      this.areaCodeError = false;
      this.fieldErrors = {};
      this.showPassword = false;
      this.showConfirmPassword = false;
    },

    showSnackbar(message, color) {
      this.snackAlert = {
        snackbar: true,
        snackMessage: message,
        snackColor: color
      };
    },

    backPageRoute() {
      this.$router.back();
    },

    normalizeRoleName(roleName) {
      return String(roleName || '').trim().toLowerCase().replace(/\s+/g, ' ');
    },

    isSuperAdminRole(roleName) {
      const normalized = this.normalizeRoleName(roleName);
      return normalized === 'super admin' || normalized === 'superadmin';
    },
  }
};
</script>

<style scoped>
.drop-area:hover {
  border-color: #7B1FA2 !important;
  background-color: #F3E5F5 !important;
}

.custom-inline-radio :deep(.v-selection-control-group) {
  flex-direction: row !important;
}

.custom-inline-radio :deep(.v-label) {
  white-space: nowrap !important;
  opacity: 1 !important;
  min-width: 35px;
}

.custom-inline-radio :deep(.v-selection-control) {
  min-width: 80px;
}

.country-code-box {
  font-size: 16px;
  color: #333;
  border-color: #B0B0B0 !important;
}

.cursor-pointer {
  cursor: pointer;
}
</style>